import React, { useState } from 'react';
import { Box, Typography, Button, Grid, useTheme, useMediaQuery, MenuItem, Select, OutlinedInput, IconButton, Autocomplete, TextField, FormHelperText, CircularProgress, Snackbar, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors, fonts } from '../../ColorFont/color';
import {
    IconMapPinFilled,
    IconCurrentLocation,
    IconMapPin,
    IconCheck,
    IconX,
    IconArrowLeft
} from '@tabler/icons-react';
import { url } from '../../ConstUrl/Url';
import NewHeaderNavWithSidebar from '../../components/HeaderNav/NewHeaderNavWithSidebar';
import { useUser } from '../../context/UserContext';
import CarbonCreditsAuthGuard from '../../components/CarbonCredits/CarbonCreditsAuthGuard';

/* 
===== UNIVERSAL TOKENS =====
Imported from currentTheme dynamically via color.js.
============================
*/

/* ===== FORM CONSTANTS ===== */
// OLD IMPLEMENTATION (Manual State List) — commented for reference
/*
const INDIA_STATES = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    ...
];
*/

// OLD IMPLEMENTATION (Manual District List) — commented for reference
/*
const DISTRICTS_MAP = { ... };
const FALLBACK_DISTRICTS = [...];
*/

// Dynamic units and ownership types will be fetched from backend config
// const LAND_UNITS = ["Acres", "Hectares", "Guntha", "Bigha", "Sq. Meters", "Sq. Feet"];
// const OWNERSHIP_TYPES = ["Owner", "Lease", "Tenant Farmer", "Co-owner", "Organization"];



const CustomInputLabel = ({ label }) => (
    <Typography sx={{
        fontFamily: fonts.publicSans || fonts.poppins,
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '14px',
        color: colors.carbon_text_main || '#0F1729',
        mb: '12px',
        display: 'block'
    }}>
        {label}
    </Typography>
);

const getInputStyles = (hasError) => ({
    height: '48px',
    borderRadius: '14px',
    backgroundColor: '#FFFFFF',
    border: `1px solid ${hasError ? '#EF4444' : (colors.carbon_border_solid || '#E0E5EB')}`,
    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
    '& fieldset': { border: 'none' },
    '&:hover': {
        border: `1px solid ${hasError ? '#EF4444' : (colors.carbon_border_solid || '#E0E5EB')}`,
    },
    '&.Mui-focused': {
        border: `1px solid ${hasError ? '#EF4444' : (colors.carbon_bg_main || '#00B4D8')}`,
    },
    fontFamily: fonts.publicSans || fonts.poppins,
    fontSize: '14px',
    color: colors.carbon_text_main || '#0F1729'
});

// ─── Restore form data from localStorage on mount (FIX: data persistence on Back) ───
const loadDraft = () => {
    try {
        const draft = JSON.parse(localStorage.getItem('carbonCreditDraft') || '{}');
        return {
            projectName: draft.projectName || '',
            state: draft.state || null,
            district: draft.district || null,
            taluka: draft.taluka || '',
            village: draft.village || '',
            gpsLocation: draft.gpsLocation || '',
            landSize: draft.landSize ? String(draft.landSize) : '',
            unit: draft.unit || 'Acres',
            ownershipType: draft.ownershipType || 'Owner'
        };
    } catch {
        return {
            projectName: '',
            state: null,
            district: null,
            taluka: '',
            village: '',
            gpsLocation: '',
            landSize: '',
            unit: 'Acres',
            ownershipType: 'Owner'
        };
    }
};

const CarbonCreditsFarmDetails = ({ userData: propUserData, onNext, onBack }) => {
    const { userData, firebaseUser, loading } = useUser();
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // FIX 3: Initialize from localStorage so Back navigation restores data
    const [formData, setFormData] = useState(loadDraft);

    const [errors, setErrors] = useState({});
    const [isLocating, setIsLocating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [statesList, setStatesList] = useState([]);
    const [districtsList, setDistrictsList] = useState([]);

    // FIX 4: Success popup state
    const [showSuccess, setShowSuccess] = useState(false);

    // Fetch dynamic config from backend
    const [config, setConfig] = useState({ 
        units: [
            { name: "Acres", factor: 1 },
            { name: "Hectares", factor: 2.47105 },
            { name: "Guntha", factor: 0.025 },
            { name: "Bigha", factor: 0.4 },
            { name: "Sq. Meters", factor: 0.000247105 },
            { name: "Sq. Feet", factor: 0.0000229568 },
        ], 
        ownershipTypes: ["Owner", "Lease", "Tenant Farmer", "Co-owner", "Organization"] 
    });

    React.useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await fetch(`${url}/api/carbon-credits/config`);
                const data = await response.json();
                if (response.ok) {
                    setConfig(data);
                }
            } catch (error) {
                console.error("Error fetching carbon credit config:", error);
            }
        };
        fetchConfig();
    }, []);

    React.useEffect(() => {
        const fetchStates = async () => {
            try {
                const response = await fetch("https://countriesnow.space/api/v0.1/countries/states", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ country: "India" })
                });
                const data = await response.json();
                if (!data.error && data.data && data.data.states) {
                    const states = data.data.states.map(state => state.name);
                    setStatesList(states);
                }
            } catch (error) {
                console.error("Error fetching states:", error);
            }
        };
        fetchStates();
    }, []);

    React.useEffect(() => {
        const fetchDistricts = async () => {
            if (!formData.state) {
                setDistrictsList([]);
                return;
            }
            try {
                const response = await fetch("https://countriesnow.space/api/v0.1/countries/state/cities", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ country: "India", state: formData.state })
                });
                const data = await response.json();
                if (!data.error && data.data) {
                    setDistrictsList(data.data);
                } else {
                    setDistrictsList([]);
                }
            } catch (error) {
                console.error("Error fetching districts:", error);
                setDistrictsList([]);
            }
        };
        fetchDistricts();
    }, [formData.state]);

    const steps = [
        { id: 1, label: t('carbonCredits.farmDetails'), isActive: true, isCompleted: false },
        { id: 2, label: "", isActive: false, isCompleted: false },
        { id: 3, label: "", isActive: false, isCompleted: false },
        { id: 4, label: "", isActive: false, isCompleted: false },
    ];

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({ ...prev, [field]: event.target.value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleAutocompleteChange = (field) => (event, newValue) => {
        setFormData(prev => {
            const newData = { ...prev, [field]: newValue };
            if (field === 'state') newData.district = null;
            return newData;
        });
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleGetLocation = () => {
        setIsLocating(true);
        const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (!isSecure) {
            alert("Location services require a secure (HTTPS) connection. Please enter your location manually.");
            setIsLocating(false);
            return;
        }
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setFormData(prev => ({
                        ...prev,
                        gpsLocation: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
                    }));
                    if (errors.gpsLocation) setErrors(prev => ({ ...prev, gpsLocation: null }));
                    setIsLocating(false);
                },
                (error) => {
                    console.warn("Could not obtain location:", error.message);
                    let msg = "Unable to retrieve location automatically. This can happen if location permissions are disabled, or your network is unstable.";
                    if (error.code === 1) msg = "Location access denied. Please enable location permissions in your browser or device settings.";
                    if (error.code === 2) msg = "Location information is unavailable at the moment. Please try again soon.";
                    if (error.code === 3) msg = "The location request timed out. Please try again or enter your coordinates manually.";
                    
                    alert(msg);
                    setIsLocating(false);
                },
                { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
            );
        } else {
            alert("Geolocation is not supported by your browser.");
            setIsLocating(false);
        }
    };

    const validateForm = () => {
        let newErrors = {};
        if (!formData.projectName?.trim()) newErrors.projectName = "Project name is required";
        if (!formData.state) newErrors.state = "State is required";
        if (!formData.district) newErrors.district = "District is required";
        if (!formData.landSize.trim()) {
            newErrors.landSize = "Land size is required";
        } else if (isNaN(formData.landSize) || Number(formData.landSize) <= 0) {
            newErrors.landSize = "Please enter a valid positive number";
        }
        if (!formData.ownershipType) newErrors.ownershipType = "Ownership type is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (validateForm()) {
            setIsSubmitting(true);
            try {
                localStorage.setItem('carbonCreditDraft', JSON.stringify({
                    ...formData,
                    landSize: Number(formData.landSize),
                    userId: userData?._id,
                    unitFactors: config.units // Save units with factors for calculation in next step
                }));

                if (onNext) onNext();
            } catch (error) {
                console.error("Error saving draft info:", error);
                alert("An error occurred while saving locally.");
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <CarbonCreditsAuthGuard 
            userData={userData} 
            firebaseUser={firebaseUser} 
            loading={loading}
        >
            <Box sx={{
            width: '100%',
            minHeight: '100vh',
            backgroundColor: '#FFFFFF',
            pb: '80px',
            position: 'relative' // needed for z-index stacking context
        }}>
            {/* Top Stepper Area */}
            <Box sx={{
                width: '100%',
                pt: isMobile ? '12px' : '16px',
                pb: isMobile ? '12px' : '14px',
                borderBottom: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
                backgroundColor: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    maxWidth: '680px',
                    px: isMobile ? '20px' : '24px'
                }}>
                    {steps.map((step, index) => (
                        <React.Fragment key={step.id}>
                            <Box sx={{
                                width: '32px',
                                height: '32px',
                                flexShrink: 0,
                                borderRadius: '50%',
                                backgroundColor: (step.isActive || step.isCompleted) ? (colors.carbon_bg_main || '#00B4D8') : (colors.carbon_step_bg_unselected || '#E2E8F0'),
                                color: (step.isActive || step.isCompleted) ? '#FFFFFF' : (colors.carbon_step_text_unselected || '#64748B'),
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 600,
                                fontSize: '14px',
                                zIndex: 2
                            }}>
                                {step.isCompleted ? <IconCheck size={18} color="#FFFFFF" stroke={2.5} /> : step.id}
                            </Box>
                            {index < steps.length - 1 && (
                                <Box sx={{
                                    flex: 1,
                                    mx: isMobile ? '8px' : '16px',
                                    height: '2px',
                                    backgroundColor: (steps[index + 1].isActive || steps[index + 1].isCompleted) ? (colors.carbon_bg_main || '#00B4D8') : (colors.carbon_step_bg_unselected || '#E2E8F0'),
                                    borderRadius: '4px'
                                }} />
                            )}
                        </React.Fragment>
                    ))}
                </Box>
                <Typography sx={{
                    mt: '16px',
                    fontFamily: fonts.publicSans || fonts.poppins,
                    fontWeight: 500,
                    fontSize: '13px',
                    color: colors.carbon_text_secondary || '#65758B',
                }}>
                    {steps.find(s => s.label)?.label || ""}
                </Typography>
            </Box>

            {/* Form Content Area */}
            <Box sx={{
                maxWidth: '680px',
                margin: '0 auto',
                px: 0,
                pt: isMobile ? '16px' : '24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}>
                {/* Header Icon & Title */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    mb: '24px'
                }}>
                    <Box sx={{ color: colors.carbon_bg_main || '#00B4D8' }}>
                        <IconMapPin size={48} stroke={1.5} />
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{
                            fontFamily: fonts.manrope || fonts.poppins,
                            fontWeight: 700,
                            fontSize: isMobile ? '24px' : '28px',
                            lineHeight: '34px',
                            color: colors.carbon_text_main || '#0F1729',
                            mb: '8px'
                        }}>
                            {t('carbonCredits.farmLocationSize')}
                        </Typography>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 400,
                            fontSize: isMobile ? '14px' : '16px',
                            lineHeight: '22px',
                            color: colors.carbon_text_secondary || '#65758B',
                            maxWidth: '480px'
                        }}>
                            {t('carbonCredits.tellUsAboutFarm')}
                        </Typography>
                    </Box>
                </Box>

                {/* Form Fields Grid */}
                <Grid container spacing={isMobile ? 3 : 4}>

                    {/* FIX 5: Project Name — full width, above State/District */}
                    <Grid item xs={12}>
                        <CustomInputLabel label={t('carbonCredits.projectName')} />
                        <OutlinedInput
                            fullWidth
                            value={formData.projectName}
                            onChange={handleInputChange('projectName')}
                            placeholder={t('carbonCredits.projectNamePlaceholder')}
                            error={!!errors.projectName}
                            sx={{
                                ...getInputStyles(!!errors.projectName),
                                '& input': { padding: '12px 14px' }
                            }}
                        />
                        {errors.projectName && <FormHelperText error sx={{ ml: 0 }}>{errors.projectName}</FormHelperText>}
                    </Grid>

                    {/* Row 1: State + District */}
                    <Grid item xs={12} sm={6}>
                        <CustomInputLabel label={t('carbonCredits.state')} />
                        <Autocomplete
                            options={statesList}
                            getOptionLabel={(option) => t('states.' + option, { defaultValue: option })}
                            value={formData.state}
                            onChange={handleAutocompleteChange('state')}
                            // FIX 1: z-index ensures dropdown renders above other elements
                            componentsProps={{
                                popper: { sx: { zIndex: 1300 } }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t('carbonCredits.selectState')}
                                    error={!!errors.state}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...getInputStyles(!!errors.state),
                                            paddingRight: '39px !important',
                                            paddingLeft: '14px !important'
                                        }
                                    }}
                                />
                            )}
                        />
                        {errors.state && <FormHelperText error sx={{ ml: 0 }}>{errors.state}</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomInputLabel label={t('carbonCredits.district')} />
                        <Autocomplete
                            options={districtsList}
                            getOptionLabel={(option) => t('districts.' + option, { defaultValue: option })}
                            value={formData.district}
                            onChange={handleAutocompleteChange('district')}
                            disabled={!formData.state}
                            componentsProps={{
                                popper: { sx: { zIndex: 1300 } }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t('carbonCredits.selectDistrict')}
                                    error={!!errors.district}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...getInputStyles(!!errors.district),
                                            paddingRight: '39px !important',
                                            paddingLeft: '14px !important',
                                            backgroundColor: !formData.state ? '#FFFFFF' : '#FFFFFF'
                                        }
                                    }}
                                />
                            )}
                        />
                        {errors.district && <FormHelperText error sx={{ ml: 0 }}>{errors.district}</FormHelperText>}
                    </Grid>

                    {/* Row 2: Taluka + Village */}
                    <Grid item xs={12} sm={6}>
                        <CustomInputLabel label={t('carbonCredits.taluka')} />
                        <OutlinedInput
                            fullWidth
                            value={formData.taluka}
                            onChange={handleInputChange('taluka')}
                            placeholder={t('carbonCredits.selectTaluka')}
                            sx={getInputStyles(false)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomInputLabel label={t('carbonCredits.village')} />
                        <OutlinedInput
                            fullWidth
                            value={formData.village}
                            onChange={handleInputChange('village')}
                            placeholder={t('carbonCredits.selectVillage')}
                            sx={getInputStyles(false)}
                        />
                    </Grid>

                    {/* Row 3: GPS Location */}
                    <Grid item xs={12}>
                        <CustomInputLabel label={t('carbonCredits.gpsLocation')} />
                        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                                <OutlinedInput
                                    fullWidth
                                    value={formData.gpsLocation}
                                    onChange={handleInputChange('gpsLocation')}
                                    placeholder={t('carbonCredits.gpsPlaceholder')}
                                    sx={getInputStyles(!!errors.gpsLocation)}
                                />
                                <Typography sx={{
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontWeight: 400,
                                    fontSize: '12px',
                                    color: colors.carbon_text_secondary || '#65758B',
                                    mt: '8px'
                                }}>
                                    {t('carbonCredits.orEnterManually')}
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={handleGetLocation}
                                disabled={isLocating}
                                sx={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '14px',
                                    border: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
                                    backgroundColor: '#FFFFFF',
                                    color: colors.carbon_text_main || '#0F1729',
                                    '&:hover': { backgroundColor: '#FFFFFF' }
                                }}
                            >
                                {isLocating ? <CircularProgress size={20} /> : <IconMapPin size={20} />}
                            </IconButton>
                        </Box>
                    </Grid>

                    {/* Row 4: Land Size + Unit */}
                    <Grid item xs={12} sm={6}>
                        <CustomInputLabel label={t('carbonCredits.landSize')} />
                        <OutlinedInput
                            fullWidth
                            type="number"
                            value={formData.landSize}
                            onChange={handleInputChange('landSize')}
                            placeholder={t('carbonCredits.landSizeValue')}
                            sx={getInputStyles(!!errors.landSize)}
                        />
                        {errors.landSize && <FormHelperText error sx={{ ml: 0 }}>{errors.landSize}</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomInputLabel label={t('carbonCredits.unit')} />
                        <Select
                            value={formData.unit}
                            onChange={handleInputChange('unit')}
                            fullWidth
                            sx={getInputStyles(false)}
                            MenuProps={{
                                // FIX 1: z-index ensures Unit dropdown doesn't get clipped under other elements
                                PaperProps: {
                                    elevation: 3,
                                    sx: { borderRadius: '12px', mt: '4px', zIndex: 1400 }
                                }
                            }}
                        >
                            {config.units.map(unit => (
                                <MenuItem key={unit.name} value={unit.name} sx={{ fontFamily: fonts.publicSans }}>{unit.name}</MenuItem>
                            ))}
                        </Select>
                    </Grid>

                    {/* Row 5: Ownership Type — FIX 1: extra mb avoids card overlap when dropdown opens */}
                    <Grid item xs={12} sm={6} sx={{ mb: { xs: '60px', sm: '80px' } }}>
                        <CustomInputLabel label={t('carbonCredits.ownershipType')} />
                        <Select
                            value={formData.ownershipType}
                            onChange={handleInputChange('ownershipType')}
                            fullWidth
                            sx={getInputStyles(!!errors.ownershipType)}
                            MenuProps={{
                                // FIX 1: Higher z-index + disablePortal=false ensures menu renders over siblings
                                PaperProps: {
                                    elevation: 3,
                                    sx: { borderRadius: '12px', mt: '4px', zIndex: 1400 }
                                },
                                disablePortal: false,
                            }}
                        >
                            {config.ownershipTypes.map(type => (
                                <MenuItem key={type} value={type} sx={{ fontFamily: fonts.publicSans }}>{type}</MenuItem>
                            ))}
                        </Select>
                        {errors.ownershipType && <FormHelperText error sx={{ ml: 0 }}>{errors.ownershipType}</FormHelperText>}
                    </Grid>
                </Grid>

                {/* Submit Action */}
                <Box sx={{ mt: '48px', width: '100%', display: 'flex', justifyContent: 'center', gap: '16px', flexDirection: isMobile ? 'column' : 'row' }}>
                    <Button
                        onClick={onBack}
                        variant="outlined"
                        startIcon={<IconArrowLeft size={18} stroke={2.5} />}
                        sx={{
                            borderRadius: '50px',
                            px: { xs: 3, md: 6 },
                            height: '52px',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '15px',
                            textTransform: 'none',
                            color: colors.carbon_text_main || '#1C252E',
                            borderColor: colors.carbon_border_solid || '#E0E5EB',
                            minWidth: { sm: '180px' },
                            '&:hover': {
                                borderColor: colors.carbon_border_selected || '#919EAB',
                                backgroundColor: '#F4F6F8',
                            },
                        }}
                    >
                        {t('carbonCredits.back')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{
                            borderRadius: '50px',
                            px: { xs: 3, md: 6 },
                            height: '52px',
                            backgroundColor: colors.carbon_bg_main || '#00B4D8',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '15px',
                            textTransform: 'none',
                            color: '#FFFFFF',
                            minWidth: { sm: '220px' },
                            boxShadow: '0px 4px 12px rgba(0, 180, 216, 0.2)',
                            '&:hover': {
                                backgroundColor: '#009DC0',
                                boxShadow: '0px 6px 16px rgba(0, 180, 216, 0.3)',
                                transform: 'translateY(-1px)'
                            },
                        }}
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t('carbonCredits.saveAndContinue')}
                    </Button>
                </Box>
            </Box>


            {/* FIX 6: Floating + button REMOVED — was a FAB that caused layout confusion */}
            {/* <Fab ... > was removed from here */}
        </Box>
        </CarbonCreditsAuthGuard>
    );
};

export default CarbonCreditsFarmDetails;
